import { supabase } from '../../lib/supabaseClient';
import { ENV } from '../../config/env';

const API_BASE_URL = ENV.API_URL;

export interface Automation {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  enabled: boolean;
  trigger_type: string;
  trigger_conditions: any;
  actions: any[];
  created_by: string;
  created_at: string;
  updated_at: string;
  last_run_at?: string;
  run_count: number;
}

export interface AutomationInput {
  organization_id: string;
  name: string;
  description?: string;
  enabled?: boolean;
  trigger_type: string;
  trigger_conditions?: any;
  actions: any[];
}

export interface AutomationLog {
  id: string;
  automation_id: string;
  organization_id: string;
  triggered_by_entity_type: string;
  triggered_by_entity_id: string;
  status: 'success' | 'failed' | 'partial';
  actions_performed: any[];
  error_message?: string;
  executed_at: string;
  duration_ms?: number;
}

class AutomationsService {
  private async getAuthToken(): Promise<string | null> {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || null;
  }

  async list(params: { organization_id: string }) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams(params as any).toString();
      
      const response = await fetch(`${API_BASE_URL}/api/project-management/automations?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      return { data: result.data as Automation[], error: result.error };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  async getById(id: string, organizationId: string) {
    try {
      const token = await this.getAuthToken();
      
      const response = await fetch(
        `${API_BASE_URL}/api/project-management/automations/${id}?organization_id=${organizationId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();
      return { data: result.data as Automation, error: result.error };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  async create(automation: AutomationInput) {
    try {
      const token = await this.getAuthToken();
      
      const response = await fetch(`${API_BASE_URL}/api/project-management/automations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(automation),
      });

      const result = await response.json();
      return { data: result.data as Automation, error: result.error };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  async update(id: string, organizationId: string, updates: Partial<Automation>) {
    try {
      const token = await this.getAuthToken();
      
      const response = await fetch(
        `${API_BASE_URL}/api/project-management/automations/${id}?organization_id=${organizationId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updates),
        }
      );

      const result = await response.json();
      return { data: result.data as Automation, error: result.error };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  async toggle(id: string, organizationId: string, enabled: boolean) {
    try {
      const token = await this.getAuthToken();
      
      const response = await fetch(
        `${API_BASE_URL}/api/project-management/automations/${id}/toggle?organization_id=${organizationId}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ enabled }),
        }
      );

      const result = await response.json();
      return { data: result.data as Automation, error: result.error };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }

  async delete(id: string, organizationId: string) {
    try {
      const token = await this.getAuthToken();
      
      const response = await fetch(
        `${API_BASE_URL}/api/project-management/automations/${id}?organization_id=${organizationId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();
      return { success: result.success, error: result.error };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  async getLogs(params: { organization_id: string; automation_id?: string; limit?: number }) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams(params as any).toString();
      
      const response = await fetch(`${API_BASE_URL}/api/project-management/automations/logs?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      return { data: result.data as AutomationLog[], error: result.error };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  }
}

export const automationsService = new AutomationsService();
